<template>
  <div class="onboarding-step">
    <OnboardingSidebar
      label="Get ready for lift off"
      heading="Setup your brand"
      subheading="Enter your brand details so we can customise our templates to fit your store..."
    >
      <template #icon>
        <img class="v-center m-0" src="@/assets/icons/ufo.svg" />
      </template>
    </OnboardingSidebar>

    <OnboardingContent>
      <div class="flex flex-wrap -mx-2">
        <div class="w-full md:w-1/2 mb-6 md:mb-10 px-2 md:pr-8">
          <div class="flex items-center mb-4">
            <label class="subtitle mb-0" for="logo">
              Store Logo
            </label>

            <button
              v-if="logoImageUrl"
              class="change-image-toggle ml-auto p-0"
              @click="onChangeImage"
            >
              {{ changeImageToggle ? "Cancel" : "Change" }}
            </button>
          </div>

          <Uploader
            id="brandUploader"
            :src="logoImageUrl"
            :inline="true"
            :edit-mode="changeImageToggle"
            @uploading-complete="onUploadingComplete"
            @uploading-failed="onUploadingFailed"
            @uploading-file="onUploadingFile"
          />
        </div>

        <div class="w-full md:w-1/2 px-2 mb-6 md:mb-10">
          <label class="subtitle">
            Store Colors
          </label>

          <BrandColors
            :primary-color="primaryColor"
            :secondary-color="secondaryColor"
            @color-selected="onColorSelected"
          />
        </div>
      </div>

      <div class="flex flex-wrap -mx-2 mb-4 md:mb-0">
        <div class="w-full md:w-1/2 mb-4 md:mb-0 px-2 md:pr-8">
          <label class="subtitle" for="contactEmail">
            Contact Email<sup class="required">
              *
            </sup>
          </label>

          <Input
            name="contactEmail"
            type="email"
            required
            class="mb-4"
            :value="contactEmail"
            :on-change="onEmailChange"
          />

          <p class="info">
            This is the email address we'll use to send your test newsletters
            to.
          </p>
        </div>

        <div class="w-full md:w-1/2 px-2">
          <label class="subtitle" for="industry-selector">
            Industry<sup v-if="!industriesQueryError" class="required">
              *
            </sup>
          </label>

          <Selector
            class="mb-4"
            name="industry-selector"
            :disabled="industriesQueryError"
            :options="industryOptions"
            :loading="$apollo.loading"
            :clearable="false"
            :placeholder="
              industriesQueryError
                ? 'Loading failed 👇'
                : 'Choose an industry...'
            "
            @input="onIndustrySelected"
          />

          <p v-if="!industriesQueryError" class="info">
            This is the industry we'll use to customise images and content for
            your newsletter.
          </p>

          <p v-else class="info error">
            Sorry we couldn't load any industries
            <button class="text-brand" @click="onRetryIndustriesClick">
              click to retry
            </button>
            or continue without choosing an industry.
          </p>
        </div>
      </div>

      <OnboardingNav
        class="mt-auto"
        :current-step="currentStep"
        :disable-next="uploadingFile"
        :next-title="
          uploadingFile
            ? 'Please wait for your logo to finish uploading...'
            : 'Create your newsletter 🚀'
        "
        next-label="Save and Continue"
        @prev="$emit('prev-step')"
        @next="onNextClick"
      />
    </OnboardingContent>
  </div>
</template>

<script>
import { Industries } from "@/graphql/queries";
import { isAuthError } from "@/lib/auth";
import Uploader from "@/components/UI/Uploader";
import Input from "@/components/UI/Input";
import BrandColors from "@/components/Brand/BrandColors";
import OnboardingContent from "@/components/Onboarding/OnboardingContent";
import OnboardingSidebar from "@/components/Onboarding/OnboardingSidebar";
import OnboardingNav from "@/components/Onboarding/OnboardingNav";
import Selector from "@/components/UI/Selector";

export default {
  apollo: {
    industries: {
      query: Industries,
      update: (data) => data.industries,
      error({ graphQLErrors }) {
        if (graphQLErrors.length) {
          // If we have an auth error return false here as the login modal should open...
          if (isAuthError(graphQLErrors)) return false;

          this.industriesQueryError = true;
        }
      },
    },
  },
  name: "Step3",
  components: {
    BrandColors,
    Input,
    Uploader,
    OnboardingContent,
    OnboardingNav,
    OnboardingSidebar,
    Selector,
  },
  props: {
    contactEmail: { default: "", type: String },
    currentStep: { type: Number, required: true },
    selectedIndustry: { default: "", type: String },
    primaryColor: { required: true, type: String },
    secondaryColor: { required: true, type: String },
    logoImageUrl: { required: true, type: String },
  },
  data() {
    return {
      changeImageToggle: false,
      industriesQueryError: false,
      uploadingFile: false,
    };
  },
  computed: {
    industryOptions() {
      return !this.$apollo.loading && this.industries ? this.industries : [];
    },
  },
  methods: {
    alert(title, text) {
      this.$modal.show("dialog", {
        title,
        text,
        buttons: [
          {
            title: "Close",
          },
        ],
      });
    },
    onChangeImage() {
      this.changeImageToggle = !this.changeImageToggle;
    },
    onColorSelected(color) {
      this.$emit("color-selected", color);
    },
    onEmailChange(e) {
      this.$emit("contact-email-updated", e.target.value);
    },
    onIndustrySelected(industry) {
      if (this.$has(industry, "id"))
        this.$emit("industry-selected", industry.id);
    },
    onNextClick() {
      if (!this.industriesQueryError && this.selectedIndustry === "") {
        this.alert("Oh no 🤨", "Please select an industry before continuing.");
        return false;
      }

      if (!this.contactEmail) {
        this.alert("Oh no 🤨", "We need an email address before continuing.");
        return false;
      } else if (!this.validEmail(this.contactEmail)) {
        this.alert(
          "Hmmm, that doesn't look right 🧐",
          "Please enter a valid email address."
        );
        return false;
      }

      this.$emit("next-step");
    },
    onUploadingComplete(event) {
      this.uploadingFile = false;
      this.changeImageToggle = false;
      this.$emit("uploading-complete", event);
    },
    onUploadingFailed() {
      this.uploadingFile = false;
    },
    onUploadingFile() {
      this.uploadingFile = true;
    },
    onRetryIndustriesClick() {
      this.$apollo.queries.industries.refetch();
      this.industriesQueryError = false;
    },
    validEmail: function(email) {
      var re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return re.test(email);
    },
  },
};
</script>

<style scoped>
.info {
  @apply text-xs text-grey-dark italic m-0;

  &.error {
    @apply text-danger-dark;
  }
}

.change-image-toggle {
  @apply p-0 m-0 text-brand outline-none;
  font-size: 14px;
  line-height: 1;
  vertical-align: text-top;

  &:hover {
    @apply underline;
  }
}
</style>
